import { Box, Collapse, MenuItem, TableCell, TableRow } from "@mui/material";
import BrotherPrint from "Pages/Printing/BrotherPrint";
import { DymoMultiPrint } from "Pages/Printing/DymoMultiPrint";
import { ZebraMultiPrint } from "Pages/Printing/ZebraMultiPrint";
import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLoader } from "Shared/Loader/LoaderContext";
import { TruncatedCell } from "Shared/TruncatedCell";
import { isJson } from "Utils/Common/Requisition";
import {
  StyledDropButtonThreeDots,
  StyledDropMenuMoreAction,
} from "Utils/Style/Dropdownstyle";
import { savePdfUrls } from "../../Redux/Actions/Index";
import PatientServices from "../../Services/PatientServices/PatientServices";
import RequisitionType from "../../Services/Requisition/RequisitionTypeService";
import PermissionComponent from "../../Shared/Common/Permissions/PermissionComponent";
import { AddIcon, CrossIcon, DoneIcon, RemoveICon } from "../../Shared/Icons";
import useLang from "./../../Shared/hooks/useLanguage";
import { AddNewInputs } from "./AddNewInputs";
import ApiDataModal from "./ApiDataModal";
import CollapseTable from "./Collapse";
import ConfirmationModal from "./ConfirmationModal";
import { useDynamicGrid } from "./Context/useDynamicGrid";
import {
  FilterTypeColumns,
  isFilterDependentDataAction,
  isMultiSelectFilterType,
  isSelectableFilterType,
  isShowHideAction,
  optionMatchesStoredRowValue,
  parseJsonOptionsArray,
  storesLabelInRow,
} from "./filterDependentData";
import SignatureModal from "./SignatureModal";

interface RowProps {
  item: any;
  loadData: (reset: boolean) => void;
  rows: any;
}

function Row({ item, loadData, rows }: RowProps) {
  const {
    setBulkIds,
    setRows,
    columnDataActions,
    bulkIds,
    isBulkEdit,
    columnActions,
    setInputFields,
    inputFields,
    bulkExportActions,
    bulkActions,
    setApiData,
    tabIdToSend,
    apiData,
    expandableColumnsHeader,
    isExpandable,
    columnsHeader,
    conditionalFields,
  } = useDynamicGrid();

  const { t } = useLang();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [openModal, setOpenModal] = useState(false);
  const [actionForModal, setActionForModal] = useState(null);
  const [signature, setSignature] = useState("");
  const [open, setOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [openApiModal, setOpenApiModal] = useState(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<any>(null);
  const [pendingData, setPendingData] = useState<any>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const actionConfigurationRef = useRef(null);
  const allowedExtensionsRef = useRef(null);
  const normalizePayloadValue = (value: any, inputType: any) => {
    const normalizedType = String(inputType ?? "").toLowerCase();
    if (normalizedType === FilterTypeColumns.MULTI_SELECT && Array.isArray(value)) {
      return value.map((opt: any) => String(opt?.value ?? ""));
    }
    if (
      normalizedType === FilterTypeColumns.MULTI_SELECT_VALUE_BY_LABEL &&
      Array.isArray(value)
    ) {
      return value.map((opt: any) => String(opt?.label ?? ""));
    }
    return value ?? "";
  };

  const normalizeRowForEdit = (row: any) => {
    const normalizedRow = { ...row };

    (inputFields || []).forEach((field: any) => {
      if (!isMultiSelectFilterType(field?.inputType)) {
        return;
      }

      const fieldKey = field?.name || field?.fieldName || field?.columnKey;
      if (!fieldKey) return;

      const rawValue = normalizedRow[fieldKey];
      if (Array.isArray(rawValue)) return;

      let options: any[] = [];
      if (field?.jsonOptionData) {
        try {
          const parsed = JSON.parse(field.jsonOptionData);
          options = Array.isArray(parsed) ? parsed : [];
        } catch {
          options = [];
        }
      }

      const selectedTokens = String(rawValue ?? "")
        .split(",")
        .map((val) => val.trim())
        .filter(Boolean);

      const matchByLabel = storesLabelInRow(field?.inputType);

      normalizedRow[fieldKey] = selectedTokens.map((token) => {
        const matchedOption = options.find((opt: any) =>
          matchByLabel
            ? String(opt?.label ?? "") === token
            : String(opt?.value ?? "") === token
        );

        return matchedOption
          ? {
            ...matchedOption,
            value: matchedOption.value,
            label: matchedOption.label ?? token,
          }
          : { value: token, label: token };
      });
    });

    return normalizedRow;
  };

  const executeAction = async (action: any, data: any) => {
    try {
      showLoader();
      const payload = {
        TableId: 0,
        actionName: action.actionName,
        tabId: tabIdToSend,
        ids: [data.Id],
      };
      const response = await PatientServices.makeApiCallForDynamicGrid(
        action.actionUrl,
        action.methodType ?? null,
        payload
      );
      if (response.data.statusCode === 200) {
        toast.success(t(response.data.responseMessage));
        loadData(false);
      }
    } catch (error) {
      console.error("Error in action API call:", error);
      toast.error(t("Failed to perform action"));
    } finally {
      hideLoader();
    }
  };

  const handleConfirmedAction = async () => {
    if (pendingAction && pendingData) {
      await executeAction(pendingAction, pendingData);
      setPendingAction(null);
      setPendingData(null);
    }
  };

  const handleActionClick = async (action: any, data: any) => {
    if (action.buttontype === 3) {
      const isAnyRowActive = rows.some((row: any) => row.rowStatus === true);

      if (isAnyRowActive) {
        console.log(
          "Another row is already active. Cannot add or activate another row."
        );
        return;
      }

      // Set initial field visibility
      let updatedFields = inputFields.map((field: any) => ({
        ...field,
        show: field?.isIndividualEditable,
      }));

      // ✅ DYNAMIC: Apply conditional field visibility based on API config
      const showHideConditionalFields = (conditionalFields || []).filter(
        (cfg: any) =>
          isShowHideAction(cfg) && Array.isArray(cfg?.Dependencies)
      );
      if (showHideConditionalFields?.length > 0) {
        // Get all dependent fields from config
        const allDependentFields = new Set<string>();
        showHideConditionalFields.forEach((cfg: any) => {
          cfg.Dependencies.forEach((dep: any) => {
            dep.DependentFields.forEach((df: any) => {
              allDependentFields.add(df.Field);
            });
          });
        });

        // Apply visibility rules based on current item values
        updatedFields = updatedFields.map((field: any) => {
          if (!field || !allDependentFields.has(field.name)) return field;

          // Default: hide dependent fields
          let shouldHide = true;

          // Check each conditional config
          showHideConditionalFields.forEach((cfg: any) => {
            const controlField = cfg.Column;
            const selectedValue = item?.[controlField];

            if (!selectedValue) return;

            const matchingDep = cfg.Dependencies.find(
              (d: any) => d.value.toString() === selectedValue.toString()
            );

            if (!matchingDep) return;

            // Find rule for this specific field
            const rule = matchingDep.DependentFields.find(
              (df: any) => df.Field === field.name
            );

            if (rule) {
              shouldHide = rule.IsDisable; // true = hidden, false = visible
            }
          });

          return { ...field, show: shouldHide };
        });
      }

      setInputFields(updatedFields);

      const updatedRows = rows.map((row: any) => {
        if (row.Id === item.Id) {
          const normalizedEditRow = normalizeRowForEdit(row);
          return { ...normalizedEditRow, rowStatus: true };
        }
        return row;
      });
      setApiData({
        url: action.actionUrl,
        body: action.jsonBody,
        method: action.methodType,
        buttontype: action.buttontype,
      });

      setRows(updatedRows);
      return;
    }

    if (action.buttontype === 5) {
      // Set initial field visibility
      let updatedFields = inputFields.map((field: any) => ({
        ...field,
        show: !field?.isIndividualEditable,
      }));

      // ✅ DYNAMIC: Apply conditional field visibility based on API config
      const showHideConditionalFields = (conditionalFields || []).filter(
        (cfg: any) =>
          isShowHideAction(cfg) && Array.isArray(cfg?.Dependencies)
      );
      if (showHideConditionalFields?.length > 0) {
        // Get all dependent fields from config
        const allDependentFields = new Set<string>();
        showHideConditionalFields.forEach((cfg: any) => {
          cfg.Dependencies.forEach((dep: any) => {
            dep.DependentFields.forEach((df: any) => {
              allDependentFields.add(df.Field);
            });
          });
        });

        // Apply visibility rules based on current item values
        updatedFields = updatedFields.map((field: any) => {
          if (!field || !allDependentFields.has(field?.name)) return field;

          // Default: hide dependent fields
          let shouldHide = true;

          // Check each conditional config
          showHideConditionalFields.forEach((cfg: any) => {
            const controlField = cfg.Column;
            const selectedValue = item?.[controlField];

            if (!selectedValue) return;

            const matchingDep = cfg.Dependencies.find(
              (d: any) => d.value.toString() === selectedValue.toString()
            );

            if (!matchingDep) return;

            // Find rule for this specific field
            const rule = matchingDep.DependentFields.find(
              (df: any) => df.Field === field?.name
            );

            if (rule) {
              shouldHide = rule.IsDisable; // true = hidden, false = visible
            }
          });

          return { ...field, show: shouldHide };
        });
      }

      setInputFields(updatedFields);

      const updatedRows = rows.map((row: any) => {
        if (row.Id === item.Id) {
          return { ...row, rowStatus: true };
        }
        return row;
      });
      setApiData({
        url: action.actionUrl,
        body: action.jsonBody,
        method: action.methodType,
        buttontype: action.buttontype,
      });

      setRows(updatedRows);
      return;
    }

    if (action.actionUrl && action.buttontype === 1) {
      const path = action.actionUrl.replace("{{Id}}", btoa(data.Id));
      navigate(path, { replace: true });
    } else if (action.actionUrl && action.buttontype === 2) {
      // Check if confirmation is required
      if (action.isTakeReason) {
        setPendingAction(action);
        setPendingData(data);
        setOpenConfirmationModal(true);
        handleClose();
        return;
      }

      // Execute action directly if no confirmation needed
      await executeAction(action, data);
    }

    if (action.buttontype === 7) {
      const path = action.actionUrl;
      const _path = path
        .replace("{{pageId}}", window.btoa(action.redirectPageId))
        .replace("{{id}}", `?id=${Math.abs(item.Id)}`);

      navigate(_path);
    }

    if (action.buttontype === 9) {
      setActionForModal(action);
      setOpenModal(true);
      handleClose();
    }
  };

  const handleIdsSelections = (id: number) => {
    setBulkIds((prevIds: number[]) => {
      if (prevIds.includes(id)) {
        return prevIds.filter((existingId) => existingId !== id);
      } else {
        return [...prevIds, id];
      }
    });
  };

  const handleRowRemove = () => {
    loadData(false);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const openDrop = Boolean(anchorEl);

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const makeAPICALL = async (item: any) => {
    try {
      showLoader();
      const isButtonType5 = apiData.buttontype === 5;
      const fieldsForPayload = isButtonType5
        ? inputFields.filter((field: any) => field?.isIndividualEditable)
        : inputFields;

      /**
       * Create map for ALL fields, including hidden ones.
       * Hidden fields will be sent with empty string values in the payload.
       * This ensures the backend receives all expected fields and can properly
       * clear/reset values when fields are disabled.
       *
       * For PageId 148, 149 and 150: When ControlType changes, hidden fields (QCText or Low/High)
       * are explicitly set to empty strings in the API request.
       *
       * Button type 5: omit isIndividualEditable fields from the map, payload, and required checks.
       */
      const nameToFieldMap = fieldsForPayload.reduce((map: any, field: any) => {
        const rawFieldName = field?.fieldName;
        const normalizedFieldName =
          rawFieldName === null || rawFieldName === undefined
            ? ""
            : String(rawFieldName).trim();

        // Skip invalid field names to prevent payload keys like "null" or ""
        if (field?.name && normalizedFieldName !== "") {
          map[field.name] = {
            fieldName: normalizedFieldName,
            isHidden: field.show, // show: true means hidden
            inputType: field?.inputType,
          };
        }
        return map;
      }, {});

      // Create map of required fields: columnKey -> columnLabel
      const requiredFieldsMap = columnsHeader?.reduce((map: any, column: any) => {
        if (
          column.isRequired === true &&
          column.isShowOnUi === true &&
          !column.isExpandData &&
          column.isShow
        ) {
          map[column.columnKey] = column.columnLabel;
        }
        return map;
      }, {}) || {};

      // Validate required fields - find only the first missing field
      let firstMissingField: string | null = null;

      const isMissingRequiredValue = (value: any, inputType: any) => {
        if (value === null || value === undefined) {
          return true;
        }

        if (typeof value === "string") {
          return value.trim() === "";
        }

        if (isMultiSelectFilterType(inputType)) {
          const matchByLabel = storesLabelInRow(inputType);
          if (Array.isArray(value)) {
            return (
              value.length === 0 ||
              value.every((selected: any) => {
                if (selected === null || selected === undefined) {
                  return true;
                }
                if (typeof selected === "string") {
                  return selected.trim() === "";
                }
                return String(
                  matchByLabel ? selected?.label ?? "" : selected?.value ?? ""
                ).trim() === "";
              })
            );
          }
          return String(value).trim() === "";
        }

        return false;
      };

      for (const columnKey of Object.keys(requiredFieldsMap)) {
        if (isButtonType5 && !nameToFieldMap[columnKey]) {
          continue;
        }

        const fieldValue = item[columnKey];
        const isFieldHidden = nameToFieldMap[columnKey]?.isHidden;
        const inputType = nameToFieldMap[columnKey]?.inputType;
        const columnLabel = requiredFieldsMap[columnKey];

        // Check if field is required and visible (not hidden)
        if (!isFieldHidden) {
          if (isMissingRequiredValue(fieldValue, inputType)) {
            firstMissingField = columnLabel;
            break;
          }
        }
      }

      if (firstMissingField) {
        hideLoader();
        toast.error(
          `${t("Please fill in required field")}: ${t(firstMissingField)}`
        );
        return;
      }

      const updatedData = Object.keys(item).reduce((result: any, key: any) => {
        const fieldInfo = nameToFieldMap[key];
        if (fieldInfo) {
          // Hidden fields are sent as "", and visible null/undefined values are normalized to ""
          result[fieldInfo.fieldName] = fieldInfo.isHidden
            ? ""
            : normalizePayloadValue(item[key], fieldInfo.inputType);
        }
        return result;
      }, {});

      // Also add any hidden fields that might not be in the item yet
      fieldsForPayload.forEach((field: any) => {
        const rawFieldName = field?.fieldName;
        const fieldName =
          rawFieldName === null || rawFieldName === undefined
            ? ""
            : String(rawFieldName).trim();
        if (field && fieldName !== "" && field.show) {
          // show: true means hidden
          if (!(fieldName in updatedData)) {
            updatedData[fieldName] = "";
          }
        }
      });

      delete updatedData.rowStatus;

      const hasValidData = Object.values(updatedData).some(
        (value: any) => {
          if (value === null || value === undefined) {
            return false;
          }
          // Handle numbers (including 0, which is a valid value)
          if (typeof value === "number") {
            return !isNaN(value);
          }
          // Handle strings
          if (typeof value === "string") {
            return value.trim() !== "";
          }
          // Handle booleans (both true and false are valid)
          if (typeof value === "boolean") {
            return true;
          }
          // For other types (objects, arrays, etc.), consider them valid if they exist
          return true;
        }
      );

      if (!hasValidData) {
        hideLoader();
        toast.error(t("Please fill in at least one field."));
        return;
      }

      const payloadToSend = {
        id: item.Id === "" ? 0 : item.Id,
        TableId: 0,
        tabId: tabIdToSend,
        jsonFields: JSON.stringify(updatedData),
      };

      const response = await PatientServices.makeApiCallForDynamicGrid(
        apiData.url,
        apiData.method ?? null,
        payloadToSend
      );

      if (response.data.statusCode === 409) {
        toast.error(t(response.data.responseMessage));
      } else if (response.data.statusCode === 200) {
        toast.success(t(response.data.responseMessage));
        loadData(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      hideLoader();
    }
  };

  const handleButtonType10 = async (item: any, actionConfiguration: any) => {
    try {
      if (!actionConfiguration) {
        toast.error(t("Action configuration is missing"));
        return;
      }

      showLoader();

      let path = actionConfiguration.URL;
      const method = actionConfiguration.APIMethod || "GET";

      // Replace all placeholders in the URL
      // Handle {Id}
      if (path.includes("{Id}")) {
        path = path.replace(/{Id}/g, `${item.Id}`);
      }

      // Handle {TypeId} - can come from item.TypeId or tabIdToSend
      if (path.includes("{ActionTypeId}")) {
        const actionTypeId = actionConfiguration.ActionTypeId;
        path = path.replace(/{ActionTypeId}/g, `${actionTypeId}`);
      }

      // Handle {TabId}
      if (path.includes("{TabId}")) {
        path = path.replace(/{TabId}/g, `${tabIdToSend}`);
      }

      // Handle {Column}
      if (path.includes("{Column}")) {
        path = path.replace(/{Column}/g, `${actionConfiguration.Column}`);
      }

      // Make the API call
      const response = await PatientServices.APICALL(path, method);

      hideLoader();

      if (response?.data) {
        setModalData(response.data);
        setOpenApiModal(true);
      } else {
        toast.error(t("No data received from API"));
      }
    } catch (error) {
      toast.error(t("Failed to fetch data"));
      console.error("Error fetching data for ButtonType 10:", error);
      hideLoader();
    }
  };

  const handleActionConfiguration = async (item: any, column: any) => {
    if (item.ActionConfiguration) {
      const actionConfigurationArray = JSON.parse(item.ActionConfiguration);
      const actionConfiguration = actionConfigurationArray.find(
        (action: any) => column?.columnKey === action.Column
      );

      actionConfigurationRef.current = actionConfiguration;

      // Store allowedExtensions in the ref
      if (actionConfiguration?.JSONRequest?.FileType) {
        allowedExtensionsRef.current =
          actionConfiguration.JSONRequest.FileType.join(",");
      } else {
        allowedExtensionsRef.current = null;
      }

      if (
        Boolean(item.IsTakeSignature) &&
        item.SignaturePreviewButtontype === 9 &&
        actionConfiguration
      ) {
        toast.error(
          t(
            "Please provide the Director's signature before validating the order."
          )
        );
        return;
      }

      if (actionConfiguration) {
        // Check ButtonType in both item and actionConfiguration
        const buttonType = String(
          actionConfiguration.ButtonType || item.ButtonType
        );

        if (buttonType === "10") {
          await handleButtonType10(item, actionConfiguration);
          return;
        }

        if (item.ButtonType === "8") {
          (fileInputRef.current as any).click(); // Trigger file selection
          return false;
        }
        // if (item.ButtonType === "2") {
        //   // Execute action directly if no confirmation needed
        //   await executeAction(action, data);
        // }
        try {
          showLoader();

          let path = actionConfiguration.URL;
          const method = actionConfiguration.APIMethod;

          path = path.replace(
            "{Id}/{TabId}/{Column}",
            `${item.Id}/${tabIdToSend}/${actionConfiguration.Column}`
          );

          let response;
          if (item.ButtonType === "2") {
            const jsonRequest = actionConfiguration?.JSONRequest
              ? JSON.parse(actionConfiguration.JSONRequest)
              : {};

            const payload = {
              TableId: 0,
              actionName: jsonRequest?.actionName || "",
              tabId: tabIdToSend,
              ids: [item.Id],
            };

            response = await PatientServices.APICALL(path, method, payload);
            toast.success(t(response.data.responseMessage));
            loadData(false);
          } else {
            response = await PatientServices.APICALL(path, method);
          }

          const dataToReplaceWith = response?.data?.data?.[0];

          const indexToReplace = rows.findIndex(
            (item: any) => item.Id === dataToReplaceWith?.Id
          );

          if (indexToReplace !== -1) {
            const updatedBooksSplice = [...rows];
            updatedBooksSplice.splice(indexToReplace, 1, dataToReplaceWith);
            setRows(updatedBooksSplice);
          }
        } catch (error) {
          console.error("Error in action configuration API call:", error);
          toast.error(t("Failed to perform action"));
        } finally {
          hideLoader();
        }
      }
    }

    if (item.SignaturePreviewConfiguration) {
      const actionConfigurationArray = JSON.parse(
        item.SignaturePreviewConfiguration
      );
      const actionConfiguration = actionConfigurationArray.find(
        (action: any) => column?.columnKey === action.Column
      );

      if (actionConfiguration && item.SignaturePreviewButtontype === 9) {
        setOpenModal(true);
        handleSignaturePreview(column);
      }
    }
  };

  const handleSignaturePreview = async (column: any) => {
    const actionConfigurationArray = JSON.parse(
      item.SignaturePreviewConfiguration
    );
    const actionConfiguration = actionConfigurationArray.find(
      (action: any) => column?.columnKey === action.Column
    );

    if (actionConfiguration) {
      try {
        showLoader();
        let path = actionConfiguration.URL;
        const method = actionConfiguration.APIMethod;

        path = path.replace("{Id}", `${item.Id}`);
        const response = await PatientServices.APICALL(path, method);
        setSignature(response?.data?.data);
      } catch (error) {
        console.error("Error fetching signature preview:", error);
        toast.error(t("Failed to load signature preview"));
      } finally {
        hideLoader();
      }
    }
  };

  const handleFileUpload = async (
    file: File,
    item: any,
    tabIdToSend: string,
    actionConfiguration: any,
    fileExtensions: string | null
  ) => {
    if (!file) return;

    // Validate file type
    if (fileExtensions) {
      const allowedExtensionsArray = fileExtensions.split(",");
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      if (
        !fileExtension ||
        !allowedExtensionsArray.includes(`.${fileExtension}`)
      ) {
        toast.error(t(`Invalid file type. Allowed types: ${fileExtensions}`));
        return; // Stop execution if the file type is invalid
      }
    }

    const {
      URL: pathTemplate,
      APIMethod: method,
      Column: column,
    } = actionConfiguration;

    // Generate the dynamic URL
    const path = pathTemplate.replace(
      "{Id}/{TabId}/{Column}",
      `${item.Id}/${tabIdToSend}/${column}`
    );

    // Prepare FormData
    const formData = new FormData();
    formData.append("Id", item.Id);
    formData.append("ColumnName", column);
    formData.append("TabId", tabIdToSend);
    formData.append("file", file);

    try {
      showLoader();
      // Make the API call
      const response = await PatientServices.APICALL(path, method, formData);

      // Update the rows with the server response
      const dataToReplaceWith = response?.data?.data?.[0];
      if (!dataToReplaceWith) return;

      const indexToReplace = rows.findIndex(
        (row: any) => row.Id === dataToReplaceWith.Id
      );

      if (indexToReplace !== -1) {
        const updatedRows = [...rows];
        updatedRows.splice(indexToReplace, 1, dataToReplaceWith);
        setRows(updatedRows);
      }
    } catch (error) {
      console.error(t("File upload failed:"), error);
      toast.error(t("File upload failed. Please try again."));
    } finally {
      hideLoader();
    }
  };

  const openFileSelection = (
    event: React.ChangeEvent<HTMLInputElement>,
    item: any
  ) => {
    const file = event.target.files?.[0];

    if (file) {
      handleFileUpload(
        file,
        item,
        tabIdToSend,
        actionConfigurationRef.current,
        allowedExtensionsRef.current
      );
    }
  };

  /**
   * buttonType 11 — Print Label.
   * Fetches ZPL/content from GetQCPrinterContent, then prints via the matching driver.
   */
  const handlePrintLabel = async (
    action: any,
    printerId: number,
    printerLabel: string
  ) => {
    try {
      showLoader();

      let payload: Record<string, any> = {};
      if (action.jsonBody) {
        try {
          payload = JSON.parse(action.jsonBody);
        } catch (parseError) {
          console.error("buttonType 11: invalid jsonBody", parseError);
        }
      }

      payload.printerId = printerId;
      payload.qcLotIds = [item.Id];

      const response = await PatientServices.makeApiCallForDynamicGrid(
        action.actionUrl,
        action.methodType ?? "POST",
        payload
      );

      if (response?.data?.httpStatusCode === 200) {
        const printContent = response?.data?.data;
        if (printContent?.length) {
          dispatchPrintLabelContent(printerLabel, printContent, t);
        }
      } else {
        toast.info(
          t(
            response?.data?.message ||
              response?.data?.responseMessage ||
              "Failed to fetch printer content"
          )
        );
      }
    } catch (error) {
      console.error("buttonType 11 — API error:", error);
      toast.error(t("Failed to fetch printer content"));
    } finally {
      hideLoader();
    }
  };

  const handleFileScenario = (action: any) => {
    if (action.buttontype === 4) {
      handleFileDownload(action);
    } else if (action.buttontype !== 11) {
      handleFileView(action);
    }
  };

  const handleFileView = async (action: any) => {
    const url = action.actionUrl.replace("{FileUrl}", item.FileUrl);

    window.open("/docs-viewer", "_blank");
    dispatch(savePdfUrls(url));
  };

  const handleFileDownload = async (action: any) => {
    const downloadButton = document.getElementsByClassName("fa-download");

    if (downloadButton) {
      const url = action.actionUrl.replace("{FileUrl}", item.FileUrl);

      RequisitionType.ShowBlob(url).then((res: any) => {
        window.open(res?.data?.Data.replace("}", ""), "_blank");
      });
    } else {
      console.log("The button does not have the download icon.");
    }
  };

  return (
    <>
      <TableRow style={{ background: item?.IsNotEditable ? "#ececec" : "" }}>
        {bulkActions?.length || bulkExportActions?.length ? (
          <TableCell>
            <label className="form-check form-check-sm form-check-solid">
              <input
                type="checkbox"
                className="form-check-input"
                checked={bulkIds.includes(item.Id)}
                onChange={() => handleIdsSelections(item.Id)}
              />
            </label>
          </TableCell>
        ) : null}
        {isExpandable() ? (
          <TableCell className="w-20px min-w-20px">
            <span onClick={() => setOpen(!open)}>
              {open ? (
                <button className="btn btn-icon btn-icon-light btn-sm fw-bold btn-table-expend-row rounded h-20px w-20px min-h-20px">
                  <RemoveICon />
                </button>
              ) : (
                <button className="btn btn-icon btn-icon-light btn-sm fw-bold btn-primary rounded h-20px w-20px min-h-20px">
                  <AddIcon />
                </button>
              )}
            </span>
          </TableCell>
        ) : null}
        {item.rowStatus ? (
          isBulkEdit ? (
            <TableCell sx={{ width: 88, minWidth: 88, maxWidth: 88 }}></TableCell>
          ) : (
            <TableCell
              sx={{ width: 88, minWidth: 88, maxWidth: 88 }}
              className="text-center"
            >
              <div className="gap-2 d-flex justify-content-center">
                <button
                  onClick={() => makeAPICALL(item)}
                  className="btn btn-icon btn-sm fw-bold btn-table-save btn-icon-light h-32px w-32px fas-icon-20px"
                >
                  <DoneIcon />
                </button>
                <button
                  onClick={() => handleRowRemove()}
                  className="btn btn-icon btn-sm fw-bold btn-table-cancel btn-icon-light h-32px w-32px fas-icon-20px"
                >
                  <CrossIcon />
                </button>
              </div>
            </TableCell>
          )
        ) : !columnActions?.length ? null : (
          <TableCell
            className="text-center"
            sx={{ width: 88, minWidth: 88, maxWidth: 88 }}
          >
            {item?.IsNotEditable ? null : (
              <div className="d-flex justify-content-center">
                <StyledDropButtonThreeDots
                  id="demo-positioned-button"
                  aria-controls={openDrop ? "demo-positioned-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={openDrop ? "true" : undefined}
                  onClick={handleClick}
                  className="btn btn-light-info btn-sm btn-icon moreactions min-w-auto rounded-4"
                >
                  <i className="bi bi-three-dots-vertical p-0 icon"></i>
                </StyledDropButtonThreeDots>
                <StyledDropMenuMoreAction
                  id="demo-positioned-menu"
                  aria-labelledby="demo-positioned-button"
                  anchorEl={anchorEl}
                  open={openDrop}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                >
                  {columnActions?.map((action: any) => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(
                      item.EventStatus,
                      "text/html"
                    );
                    const doc2 = parser.parseFromString(
                      action.actionHtml,
                      "text/html"
                    );
                    const eventStatusText = doc.body.textContent || "";
                    const actionText = doc2.body.textContent || "";

                    if (action.buttontype === 9 && !item.IsTakeSignature) {
                      return null;
                    }

                    return eventStatusText === actionText ? null : (
                      <PermissionComponent
                        moduleName={action.moduleName}
                        pageName={action.pageName}
                        permissionIdentifier={action.permissionIdentifier}
                      >
                        <MenuItem
                          onClick={(event: React.MouseEvent<HTMLElement>) => {
                            event.preventDefault();
                            handleActionClick(action, item);
                          }}
                          className="w-auto"
                          sx={{ padding: 0, margin: 0 }}
                        >
                          <div
                            dangerouslySetInnerHTML={{
                              __html: action.actionHtml,
                            }}
                          />
                        </MenuItem>
                      </PermissionComponent>
                    );
                  })}
                </StyledDropMenuMoreAction>
              </div>
            )}
          </TableCell>
        )}
        {columnsHeader?.map((column: any, columnHeaderIndex: number) => {
          if (column?.isShowOnUi && !column?.isExpandData && column.isShow) {
            if (item.rowStatus && !inputFields[columnHeaderIndex].show) {
              return (
                <AddNewInputs
                  key={column?.columnKey + columnHeaderIndex}
                  item={item}
                  column={column}
                  columnHeaderIndex={columnHeaderIndex}
                />
              );
            } else {
              let keyData = item[column?.columnKey];
              const isItBlobFile =
                keyData && typeof keyData === "string"
                  ? keyData.includes("https://truemedpo.blob.core.windows.net")
                  : false;

              const field = inputFields?.[columnHeaderIndex];

              if (
                field?.jsonOptionData &&
                isSelectableFilterType(field?.inputType) &&
                !storesLabelInRow(field?.inputType)
              ) {
                try {
                  let parsedData = JSON.parse(field.jsonOptionData);

                  // Apply the same filterOn dependent filtering used in AddNewInputs
                  const dependentConfigs = (conditionalFields || []).filter(
                    (cfg: any) =>
                      isFilterDependentDataAction(cfg) &&
                      Array.isArray(cfg?.DependentFilteredFields) &&
                      cfg.DependentFilteredFields.some(
                        (dep: any) => dep?.Field === column?.columnKey
                      )
                  );

                  if (dependentConfigs.length > 0) {
                    let hasAppliedDependentFilter = false;

                    dependentConfigs.forEach((cfg: any) => {
                      const depRule = cfg.DependentFilteredFields.find(
                        (dep: any) => dep?.Field === column?.columnKey
                      );
                      const filterOn = depRule?.FilterOn;
                      const controllingColumn = cfg?.Column;
                      if (!filterOn || !controllingColumn) return;

                      const selectedControlValue = item?.[controllingColumn];
                      let filterValue: any = undefined;

                      // 1) Try control option metadata first
                      const controlField = (inputFields || []).find(
                        (f: any) => f?.name === controllingColumn
                      );
                      const controlOptions = parseJsonOptionsArray(controlField?.jsonOptionData);
                      const selectedControlOption = controlOptions.find((opt: any) =>
                        optionMatchesStoredRowValue(
                          opt,
                          selectedControlValue,
                          controlField?.inputType
                        )
                      );
                      if (selectedControlOption && selectedControlOption?.[filterOn] != null) {
                        filterValue = selectedControlOption[filterOn];
                      }

                      // 2) Fallback to selected control value itself
                      if (
                        (filterValue === undefined || filterValue === null || filterValue === "") &&
                        selectedControlValue !== undefined &&
                        selectedControlValue !== null &&
                        selectedControlValue !== ""
                      ) {
                        filterValue = selectedControlValue;
                      }

                      // 3) Fallback to row[FilterOn] if available
                      if (
                        (filterValue === undefined || filterValue === null || filterValue === "") &&
                        item?.[filterOn] !== undefined &&
                        item?.[filterOn] !== null &&
                        item?.[filterOn] !== ""
                      ) {
                        filterValue = item?.[filterOn];
                      }

                      if (filterValue === undefined || filterValue === null || filterValue === "") {
                        return;
                      }

                      hasAppliedDependentFilter = true;
                      parsedData = parsedData.filter((opt: any) =>
                        String(opt?.[filterOn] ?? "") === String(filterValue ?? "")
                      );
                    });

                    if (!hasAppliedDependentFilter) {
                      parsedData = [];
                    }
                  }

                  if (isMultiSelectFilterType(field?.inputType)) {
                    const selectedValues = Array.isArray(keyData)
                      ? keyData.flatMap((value: any) => {
                        if (typeof value === "object" && value !== null) {
                          return String(value?.value ?? "")
                            .split(",")
                            .map((val) => val.trim())
                            .filter(Boolean);
                        }
                        return String(value ?? "")
                          .split(",")
                          .map((val) => val.trim())
                          .filter(Boolean);
                      })
                      : String(keyData ?? "")
                        .split(",")
                        .map((val) => val.trim())
                        .filter(Boolean);

                    keyData = parsedData
                      .filter((item: any) =>
                        selectedValues.includes(String(item?.value ?? ""))
                      )
                      .map((item: any) => item?.label)
                      .filter(Boolean)
                      .join(", ");
                  } else {
                    const match = parsedData.find(
                      (item: any) => item.value === keyData
                    );
                    keyData = match?.label || "";
                  }
                } catch (error) {
                  console.error("Failed to parse jsonOptionData:", error);
                  keyData = "";
                }
              }

              return (
                <TableCell key={column?.columnKey + columnHeaderIndex}>
                  {containsHTMLUsingDOMParser(item[column?.columnKey]) ? (
                    <div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={(event) => openFileSelection(event, item)}
                      />

                      <span
                        dangerouslySetInnerHTML={{ __html: keyData }}
                        onClick={() => {
                          handleActionConfiguration(item, column);
                        }}
                      />
                    </div>
                  ) : keyData === "" && columnDataActions.length ? (
                    columnDataActions.map((data: any, index: number) => {
                      if (data.columnName !== column?.columnKey) {
                        return null;
                      }

                      if (data.buttontype === 11) {
                        return (
                          <PrintLabelCell
                            key={data.columnName + index}
                            column={column}
                            action={data}
                            onPrint={handlePrintLabel}
                          />
                        );
                      }

                      return (
                        <span
                          key={data.columnName + index}
                          dangerouslySetInnerHTML={{
                            __html: data.actionHtml,
                          }}
                          onClick={() => handleFileScenario(data)}
                        />
                      );
                    })
                  ) : isJson(keyData) ? (
                    <JsonHandling value={keyData} />
                  ) : isItBlobFile ? (
                    <Link
                      className="d-flex justify-content-center align-items-center"
                      to={`/docs-viewer`}
                      target="_blank"
                    >
                      <i
                        className="bi bi-file-earmark-pdf text-danger fa-2x cursor-pointer"
                        onClick={() => {
                          dispatch(savePdfUrls(keyData));
                        }}
                      ></i>
                    </Link>
                  ) : (
                    <TruncatedCell text={keyData} />
                  )}
                </TableCell>
              );
            }
          }
          return null; // Ensure map() always returns something
        })}
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} className="padding-0">
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <CollapseTable
                patientId={item.PatientId}
                expandableColumnsHeader={expandableColumnsHeader}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <SignatureModal
        item={item}
        loadData={loadData}
        signature={signature}
        openModal={openModal}
        setOpenModal={setOpenModal}
        setSignature={setSignature}
        actionForModal={actionForModal}
      />
      <ApiDataModal
        openModal={openApiModal}
        setOpenModal={setOpenApiModal}
        modalData={modalData}
        loadData={loadData}
        itemId={item.Id}
        tabIdToSend={tabIdToSend}
      />
      <ConfirmationModal
        open={openConfirmationModal}
        onClose={() => {
          setOpenConfirmationModal(false);
          setPendingAction(null);
          setPendingData(null);
        }}
        onConfirm={handleConfirmedAction}
        title={
          pendingAction?.actionName
            ? `${pendingAction.actionName} Record`
            : "Confirm Action"
        }
        message={`Are you sure you want to ${pendingAction?.actionName?.toLowerCase() || "perform this action on"} this record?`}
        confirmButtonText={pendingAction?.actionName || "Confirm"}
        cancelButtonText="Cancel"
      />
    </>
  );
}

/** buttonType 11 — Print Label button; printer options open in a menu on click. */
function PrintLabelCell({
  column,
  action,
  onPrint,
}: {
  column: any;
  action: any;
  onPrint: (action: any, printerId: number, printerLabel: string) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const options = React.useMemo(
    () => parseJsonOptionsArray(column?.jsonOptionData),
    [column?.jsonOptionData]
  );

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => setAnchorEl(null);

  const handleSelect = (option: any) => {
    closeMenu();
    onPrint(action, Number(option.value), String(option.label ?? ""));
  };

  return (
    <>
      <span
        dangerouslySetInnerHTML={{ __html: action.actionHtml }}
        onClick={openMenu}
      />
      <StyledDropMenuMoreAction
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {options.map((option: any) => (
          <MenuItem
            key={String(option.value)}
            onClick={() => handleSelect(option)}
          >
            {option.label}
          </MenuItem>
        ))}
      </StyledDropMenuMoreAction>
    </>
  );
}

export default Row;

/** Routes label content to the printer driver based on the selected printer name. */
function dispatchPrintLabelContent(
  printerLabel: string,
  content: string[],
  t: (key: string) => string
) {
  const label = String(printerLabel ?? "").toLowerCase();

  if (label.includes("zebra")) {
    ZebraMultiPrint(content);
    return;
  }
  if (label.includes("dymo")) {
    DymoMultiPrint(content);
    return;
  }
  if (label.includes("brother")) {
    BrotherPrint(content);
    return;
  }

  toast.error(t("Configuration not available"));
}

const containsHTMLUsingDOMParser = (str: string): boolean => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");

  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
};

export const JsonHandling: React.FC<{ value: string }> = ({ value }) => {
  if (!value) return <></>;

  try {
    const parsedJsonValue = JSON.parse(value);

    if (Array.isArray(parsedJsonValue)) {
      // Handle JSON that is an array at the root level
      return (
        <ul>
          {parsedJsonValue.map((item, index) => (
            <li key={index}>{JSON.stringify(item)}</li>
          ))}
        </ul>
      );
    } else if (
      typeof parsedJsonValue === "object" &&
      parsedJsonValue !== null
    ) {
      // Handle JSON objects at the root level
      return <pre>{JSON.stringify(parsedJsonValue, null, 2)}</pre>;
    }

    return <div>Invalid JSON structure</div>;
  } catch (error) {
    return <div>Invalid JSON: {JSON.stringify(error)}</div>;
  }
};
